/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import './temperatures.scss';

/* eslint-disable import/no-unresolved, import/default */

import temperaturesTemplate from './temperatures.tpl.html';
import {displayTelemetryTooltip} from "../../../../../utils/tooltip-utility";

/* eslint-enable import/no-unresolved, import/default */

export default angular.module('agrolog.client.temperatures', [])
    .directive('agTemperatures', Temperatures)
    .name;

/*@ngInject*/
function Temperatures() {
    return {
        restrict: "E",
        scope: true,
        bindToController: {
            ctx: '=',
            maxTelemetry: '=',
            telemetryKeys: '=',
            absolute: '=',
            isDelta: '=',
            isOverview: '=',
            asset: '=',
            telemetryType: '<'
        },
        controller: TemperaturesController,
        controllerAs: 'vm',
        templateUrl: temperaturesTemplate
    };
}

/* eslint-disable angular/angularelement */

/*@ngInject*/
function TemperaturesController($scope, $log, $filter, $compile, $document, $translate, $element, agTypes, types, unitService) {

    let vm = this;

    vm.temperaturesLoading = true;
    vm.resolveIcon = resolveIcon;
    vm.subscriptionOptions = {
        callbacks: {
            onDataUpdated: onDataUpdated,
            onDataUpdateError: onDataUpdateError,
            dataLoading: function(subscription) {
                vm.temperaturesLoading = subscription.loadingData;
            }
        },
        useDashboardTimewindow: false
    };

    vm.tempColor = tempColor;
    vm.displayTempValueTooltip = displayTempValueTooltip;
    vm.formatValue = formatValue;
    vm.agTypes = agTypes;
    vm.cardElement = angular.element('.ag-temperatures', $element);
    vm.resized = false;

    vm.randomId = Math.random() * Math.random();
    var prevDataJson = null;

    $scope.$watch('vm.asset', function() {
        if (vm.asset) {
            reset();
            vm.temperaturesLoading = true;
            var temperaturesSubscriptionsInfo = [{
                type: types.datasourceType.entity,
                entityType: types.entityType.asset,
                entityId: vm.asset.id.id,
                entityName: vm.asset.name,
                timeseries: [
                    {name: vm.telemetryKeys[0]},
                    {name: vm.telemetryKeys[1]},
                    {name: vm.telemetryKeys[2]}
                ]
            }];
            vm.subscriptionOptions.timeWindowConfig = angular.copy(vm.ctx.dashboardTimewindow);
            vm.subscriptionOptions.timeWindowConfig.aggregation.type = types.aggregation.none.value;
            vm.subscriptionOptions.timeWindowConfig.aggregation.limit = 1;
            vm.ctx.subscriptionApi.createSubscriptionFromInfo (
                types.widgetType.timeseries.value, temperaturesSubscriptionsInfo, vm.subscriptionOptions, false, true).then(
                function(subscription) {
                    vm.subscription = subscription;
                }
            );
        }
    });

    function onResize() {
        if (!vm.temperaturesLoading) {
            vm.resized = true;
        }
    }

    addResizeListener(vm.cardElement[0], onResize); // eslint-disable-line no-undef

    function tempColor(val) {
        if (val >= vm.maxTelemetry) {
            return agTypes.colors.errorColor;
        } else {
            return agTypes.colors.normalGreyColor;
        }
    }

    function displayTempValueTooltip($event, tempVal, tempDisp, tempTs) {
        displayTelemetryTooltip($event, $scope, $compile, tempVal, tempDisp, tempTs, tempColor(tempVal));
    }

    $scope.$on('dashboardTimewindowChanged', function (event, newDashboardTimewindow) {
        if (vm.subscription) {
            var newTimewindowConfig = angular.copy(newDashboardTimewindow);
            newTimewindowConfig.aggregation.type = types.aggregation.none.value;
            newTimewindowConfig.aggregation.limit = 1;
            vm.subscription.timeWindowConfig = newTimewindowConfig;
        }
    });

    $scope.$on('$destroy', function() {
        reset();
        removeResizeListener(vm.cardElement[0], onResize); // eslint-disable-line no-undef
    });

    function reset() {
        if (vm.subscription) {
            vm.ctx.subscriptionApi.removeSubscription(vm.subscription.id);
        }
        resetValues();
    }

    function resetValues() {
        vm.minVal = 0;
        vm.minTs = '';
        vm.minT = '';
        vm.minValIndicatorPct = 0;

        vm.avgVal = 0;
        vm.avgTs = '';
        vm.avgT = '';
        vm.avgValIndicatorPct = 0;

        vm.maxVal = 0;
        vm.maxTs = '';
        vm.maxT = '';
        vm.maxValIndicatorPct = 0;
    }

    function onDataUpdated(subscription, apply) {
        var data = subscription.data;

        var isDataMatching = angular.toJson(data) === prevDataJson;
        if (isDataMatching) {
            return;
        }
        prevDataJson = angular.toJson(data);

        for (var i = 0; i < data.length; i++) {
            var datasourceData = data[i];
            var val = getNumberValue(datasourceData);
            var ts = getFormattedTs(datasourceData);
            switch (datasourceData.dataKey.name) {
                case vm.telemetryKeys[0]:
                    vm.minVal = val;
                    vm.minT = formatValue(val);
                    vm.minTs = ts;
                    break;
                case vm.telemetryKeys[1]:
                    vm.avgVal = val;
                    vm.avgT = formatValue(val);
                    vm.avgTs = ts;
                    break;
                case vm.telemetryKeys[2]:
                    vm.maxVal = val;
                    vm.maxT = formatValue(val);
                    vm.maxTs = ts;
                    break;
            }
        }

        const minV = Math.min(vm.minVal, 0);
        const maxV = Math.max(vm.maxVal, vm.maxTelemetry);
        const diff = maxV - minV;

        const minVInd = vm.minVal - minV;
        const avgVInd = vm.avgVal - minV;
        const maxVInd = vm.maxVal - minV;
        const maxTInd = vm.maxTelemetry - minV;

        vm.minValIndicatorPct = parseFloat((Math.abs(minVInd) / diff * 100).toFixed(2));
        vm.avgValIndicatorPct = parseFloat((Math.abs(avgVInd) / diff * 100).toFixed(2));
        vm.maxValIndicatorPct = parseFloat((Math.abs(maxVInd) / diff * 100).toFixed(2));
        vm.maxTelIndicatorPct = parseFloat((Math.abs(maxTInd) / diff * 100).toFixed(2));

        if (apply) {
            $scope.$digest();
        }
    }

    function onDataUpdateError(subscription, e) {
        $log.error(e);
    }

    function getNumberValue(keyData) {
        if (keyData && keyData.data && keyData.data[0] && angular.isDefined(keyData.data[0][1])) {
            if (!isNaN(keyData.data[0][1])) {
                return Number(keyData.data[0][1]);
            } else {
                return 0;
            }
        } else {
            return 0;
        }
    }

    function getFormattedTs(keyData) {
        if (keyData && keyData.data && keyData.data[0] && angular.isDefined(keyData.data[0][0])) {
            if (!isNaN(keyData.data[0][0])) {
                var ts = Number(keyData.data[0][0]);
                return $filter('date')(ts, 'yyyy-MM-dd HH:mm:ss');
            } else {
                return '';
            }
        } else {
            return '';
        }
    }

    function formatValue(value) {
        if (value === null || angular.isUndefined(value)) {
            return '';
        }
        let result = '';
        if (vm.telemetryType === agTypes.telemetry.type.temperature) {
            result = vm.ctx.utils.formatValue(unitService.toUserTemperature(value, vm.absolute), 1, '');
        } else if (vm.telemetryType === agTypes.telemetry.type.moisture) {
            result = parseFloat(value.toFixed(1));
        } else if (vm.telemetryType === agTypes.telemetry.type.co2) {
            result = parseFloat(value.toFixed(1));
        }

        return result;
    }

    function resolveIcon() {
        if (vm.telemetryType === agTypes.telemetry.type.temperature) {
            return vm.isDelta ? 'ag:delta_thermometer' : 'ag:thermometer';
        } else if (vm.telemetryType === agTypes.telemetry.type.moisture) {
            return vm.isDelta ? 'ag:delta_moisture' : 'ag:moisture';
        } else if (vm.telemetryType === agTypes.telemetry.type.co2) {
            return vm.isDelta ? 'ag:delta_co2_level' : 'ag:co2_level';
        } else {
            return '';
        }
    }
}
